import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { useMemo, useState } from "react";
import { showCustomConfirmDialog } from "../../../../common/components/dialogs/AppConfirmDialog";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { IPlannerEventUserModel } from "../models/plannerEventUserModel";
import { EventType, UserEventStatus } from "../../../../common/enums";
import { useNavigate } from "react-router-dom";
import { routePaths } from "../../../../common/infrastructure/routes/routePaths";
import { IPlannerEventModel } from "../models/plannerEventModel";

export const usePlannerEventReservationDetails = (
  userEventId: number,
  onHide: () => void,
  users?: IPlannerEventUserModel[],
  event?: IPlannerEventModel,
) => {
  const navigate = useNavigate();
  const { mutateAsync } = useApiMutation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMarkingPenaltyCancelled, setIsMarkingPenaltyCancelled] =
    useState(false);
  const user = useMemo(() => {
    return users?.find((x) => x.userEventId === userEventId);
  }, [userEventId, users]);

  const isConfirmedAndPrepaid =
    user?.status === UserEventStatus.Confirmed &&
    user?.paymentType != null &&
    (user?.pricePaid ?? 0) > 0;

  const disablePermanentDeletion =
    user?.status === UserEventStatus.OnlyPaid ||
    user?.status === UserEventStatus.Arrived ||
    isConfirmedAndPrepaid;

  const onTryPermamentDeletion = () => {
    showCustomConfirmDialog(
      "Сигурни ли сте, че искате да изтриете тази резервация завинаги? Това действие е напълно необратимо.",
      onPermanentUserDeletion,
      () => {},
      "warning",
      "Да",
      "Не",
    );
  };

  const onPermanentUserDeletion = async () => {
    setIsDeleting(true);
    try {
      await mutateAsync(
        {
          endpoint: `${endpointsMetadata.planner.permanentlyDeleteUserEvent}?userEventId=${userEventId}`,
          method: "delete",
          globalIsLoading: false,
          invalidateKeys: [[endpointsMetadata.planner.getEvent]],
        },
        {
          onSuccess: () => {
            if (
              event?.type === EventType.Individual ||
              event?.type === EventType.Service
            ) {
              navigate(routePaths.calendar);
            } else {
              onHide();
            }
          },
          onSettled: () => {
            setIsDeleting(false);
          },
        },
      );
    } catch (error) {
      //error already handled.
    }
  };

  const onTryPenaltyCancellation = () => {
    showCustomConfirmDialog(
      "Искате ли да маркирате потребителя като нередовен?",
      () => onPenaltyCancellation(true),
      () => onPenaltyCancellation(false),
      "warning",
      "Да, маркирай като нередовен",
      "Не, не го маркирай",
    );
  };

  const onPenaltyCancellation = async (markUserAsUnreliable: boolean) => {
    setIsMarkingPenaltyCancelled(true);
    try {
      await mutateAsync(
        {
          endpoint: `${endpointsMetadata.planner.markReservationAsPenaltyCancelled}?userEventId=${userEventId}&markUserAsUnreliable=${markUserAsUnreliable}`,
          method: "put",
          globalIsLoading: false,
          invalidateKeys: [
            [endpointsMetadata.planner.getEvent],
            [endpointsMetadata.authentication.getUserProfile],
          ],
        },
        {
          onSuccess: () => {
            onHide();
          },
          onSettled: () => {
            setIsMarkingPenaltyCancelled(false);
          },
        },
      );
    } catch (error) {
      //error already handled.
    }
  };

  return {
    user,
    isDeleting,
    isMarkingPenaltyCancelled,
    disablePermanentDeletion,
    onTryPermamentDeletion,
    onTryPenaltyCancellation,
  };
};
